import type { ReactElement } from 'react';
import { captureException } from '@ifixit/sentry';
import {
   MutationCache,
   QueryCache,
   QueryClient,
   QueryClientProvider,
   QueryFunction,
   QueryKey,
} from '@tanstack/react-query';
import type { FetchInit, IFixitAPIClient } from '@ifixit/ifixit-api-client';

// Most React entries import this module but never run a default query,
// so the client loads on the first one instead of joining every bundle.
let apiClientPromise: Promise<IFixitAPIClient> | undefined;
function getApiClient(): Promise<IFixitAPIClient> {
   apiClientPromise ??= import('@ifixit/ifixit-api-client').then(
      ({ IFixitAPIClient }) =>
         new IFixitAPIClient({
            origin: window.location.origin,
            headers: { 'Api-Client': 'iFixit-Web' },
         }),
      error => {
         // A failed chunk load should not fail every later query.
         apiClientPromise = undefined;
         throw error;
      }
   );
   return apiClientPromise;
}

/**
 * This default query function allows us to use the `useQuery` hook with only the API route:
 * queryKey[0] becomes `GET /api/2.0/<key>` and the query resolves to the parsed JSON body.
 * A non-2xx response rejects with a FetchError from @ifixit/ifixit-api-client.
 * Example:
 *    const { data, isLoading, error } = useQuery({ queryKey: ['guides/123']});
 */
const defaultQueryFn: QueryFunction<unknown, QueryKey> = async ({ queryKey, signal }) => {
   const endpoint = String(queryKey[0]);
   const client = await getApiClient();
   // The timing stat groups by the endpoint's first path segment: guides, users, wikis.
   const statName = endpoint.replace(/^\//, '').split(/[/?]/)[0] || 'root';
   // React Query already retries; a second layer inside the client would
   // multiply them (3 x 4 attempts against a 500).
   const init: FetchInit = { signal, retry: { maxRetries: 0 } };
   return client.get(endpoint, { statName }, init);
};

export const queryClient = new QueryClient({
   defaultOptions: {
      queries: {
         queryFn: defaultQueryFn,
      },
   },
   queryCache: new QueryCache({
      onError: (error, query) => {
         captureException(error, scope => {
            scope.setTag('react-query', 'query');
            scope.setContext('Query', { queryKey: query.queryKey });
            return scope;
         });
         console.error(error, query);
      },
   }),
   mutationCache: new MutationCache({
      onError: (error, variables, context, mutation) => {
         captureException(error, scope => {
            scope.setTag('react-query', 'mutation');
            scope.setContext('Mutation', {
               variables: JSON.stringify(variables, null, 2),
               context: JSON.stringify(context, null, 2),
               mutationKey: mutation.options.mutationKey,
            });
            return scope;
         });
         console.error(error, variables, context, mutation);
      },
   }),
});

export function initializeReactQuery(component: ReactElement<any>): ReactElement<any> {
   return <QueryClientProvider client={queryClient}>{component}</QueryClientProvider>;
}
